/* eslint-disable react/require-default-props */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Button } from '@ui/atoms/Button';
import { Popup } from '../../../Popup';
import { Checkbox } from '../../../inputs/Checkbox';
import './ColumnVisibilityStyles.scss';
const DEFAULT_LABELS = {
    buttonAriaLabel: 'Configure',
    searchPlaceholder: 'Search',
    apply: 'Apply',
    restoreDefaults: 'Reset',
    noResults: 'No columns match'
};
const baseClass = 'qmb-ui-column-visibility';
function withRequiredKeys(keys, columns) {
    const requiredKeys = columns.filter((column) => column.required).map((column) => column.key);
    return Array.from(new Set([...keys, ...requiredKeys]));
}
/**
 * ColumnVisibilityControl
 *
 * A trigger button + popup for selecting which table columns are visible.
 * Checkbox changes call `onApply` immediately. Required columns are always
 * visible and cannot be deselected. Reset restores the default set.
 *
 * ### Self-contained mode (default)
 * Renders its own toolbar trigger button. Open/close state is managed internally.
 *
 * ### Controlled / per-header mode
 * When `open`, `anchor`, and `onOpenChange` are all provided the toolbar button
 * is hidden and the popup is driven by the parent. Use this to wire per-column
 * 3-dot triggers in Table headers while keeping a single shared popup instance.
 *
 * @version 2.1.0
 * @since 2026-08-04
 */
export function ColumnVisibilityControl({ columns, visibleColumnKeys, defaultColumnKeys, onApply, labels = {}, className = '', open: controlledOpen, anchor: externalAnchor, onOpenChange, grouping }) {
    var _a, _b, _c, _d;
    const l = Object.assign(Object.assign({}, DEFAULT_LABELS), labels);
    const isControlled = controlledOpen !== undefined;
    const [internalOpen, setInternalOpen] = useState(false);
    const [search, setSearch] = useState('');
    const [groupBySubmenuOpen, setGroupBySubmenuOpen] = useState(false);
    const groupByRowRef = useRef(null);
    const buttonRef = useRef(null);
    const instanceIdRef = useRef(`col-vis-${Math.random().toString(36).slice(2)}`);
    const effectiveOpen = isControlled ? controlledOpen !== null && controlledOpen !== void 0 ? controlledOpen : false : internalOpen;
    const effectiveAnchor = externalAnchor !== null && externalAnchor !== void 0 ? externalAnchor : buttonRef.current;
    const prevControlledOpenRef = useRef(false);
    useEffect(() => {
        if (isControlled && controlledOpen && !prevControlledOpenRef.current) {
            setSearch('');
        }
        if (isControlled) {
            prevControlledOpenRef.current = controlledOpen !== null && controlledOpen !== void 0 ? controlledOpen : false;
        }
    }, [isControlled, controlledOpen]);
    const openPopup = useCallback(() => {
        setSearch('');
        setInternalOpen(true);
    }, []);
    const closePopup = useCallback(() => {
        setSearch('');
        setGroupBySubmenuOpen(false);
        if (isControlled) {
            onOpenChange === null || onOpenChange === void 0 ? void 0 : onOpenChange(false);
        }
        else {
            setInternalOpen(false);
        }
    }, [isControlled, onOpenChange]);
    const handleToggle = useCallback((key, checked) => {
        const nextKeys = checked
            ? [...visibleColumnKeys, key]
            : visibleColumnKeys.filter((visibleKey) => visibleKey !== key);
        onApply(withRequiredKeys(nextKeys, columns));
    }, [columns, onApply, visibleColumnKeys]);
    const handleRestoreDefaults = useCallback(() => {
        onApply(withRequiredKeys(defaultColumnKeys, columns));
        // Decision 17: Reset also clears grouping when it is present
        grouping === null || grouping === void 0 ? void 0 : grouping.onChange(null);
        closePopup();
    }, [columns, defaultColumnKeys, onApply, closePopup, grouping]);
    const filteredColumns = search.trim()
        ? columns.filter((col) => col.label.toLowerCase().includes(search.trim().toLowerCase()))
        : columns;
    return (React.createElement("div", { className: [baseClass, className].filter(Boolean).join(' ') },
        !isControlled && (React.createElement(Button, { ref: buttonRef, type: "button", isMenu: true, className: `${baseClass}__button`, "aria-label": l.buttonAriaLabel, "aria-expanded": internalOpen, "aria-haspopup": "dialog", onClick: internalOpen ? closePopup : openPopup },
            React.createElement("i", { className: "fa-light fa-sliders", "aria-hidden": "true" }),
            l.buttonAriaLabel)),
        React.createElement(Popup, { anchor: effectiveAnchor, show: effectiveOpen, onClose: closePopup, position: "bottom-right", containerClass: `${baseClass}__popup` },
            React.createElement("div", { className: `${baseClass}__popup-inner`, role: "dialog", "aria-label": l.buttonAriaLabel },
                React.createElement(Popup.SearchInput, { value: search, onChange: setSearch, placeholder: l.searchPlaceholder }),
                React.createElement("ul", { className: `${baseClass}__column-list` },
                    filteredColumns.map((col) => {
                        const isChecked = col.required || visibleColumnKeys.includes(col.key);
                        return (React.createElement("li", { key: col.key, className: `${baseClass}__column-item` },
                            React.createElement(Checkbox, { id: `${instanceIdRef.current}-${col.key}`, checked: isChecked, disabled: col.required, onChange: (checked) => handleToggle(col.key, checked), label: col.label })));
                    }),
                    filteredColumns.length === 0 && React.createElement("li", { className: `${baseClass}__no-results` }, l.noResults)),
                grouping && (React.createElement("div", { className: `${baseClass}__group-by` },
                    React.createElement("div", { className: `${baseClass}__group-by-row` },
                        React.createElement("button", { ref: groupByRowRef, type: "button", className: `${baseClass}__group-by-trigger`, "aria-haspopup": "menu", "aria-expanded": groupBySubmenuOpen, onClick: () => setGroupBySubmenuOpen((prev) => !prev) },
                            React.createElement("span", { className: `${baseClass}__group-by-label` }, (_b = (_a = grouping.labels) === null || _a === void 0 ? void 0 : _a.groupBy) !== null && _b !== void 0 ? _b : 'Group by'),
                            grouping.value !== null && (React.createElement("span", { className: `${baseClass}__group-by-value` }, (_d = (_c = grouping.options.find((o) => o.value === grouping.value)) === null || _c === void 0 ? void 0 : _c.label) !== null && _d !== void 0 ? _d : '')),
                            React.createElement("i", { className: "fa-light fa-chevron-right", "aria-hidden": "true" })),
                        grouping.value !== null && (React.createElement(Button, { type: "button", className: `${baseClass}__group-by-clear`, "aria-label": "Clear group by", onClick: (e) => {
                                e.stopPropagation();
                                grouping.onChange(null);
                                setGroupBySubmenuOpen(false);
                            } },
                            React.createElement("i", { className: "fa-light fa-xmark", "aria-hidden": "true" })))),
                    React.createElement(Popup, { anchor: groupByRowRef.current, show: groupBySubmenuOpen, onClose: () => setGroupBySubmenuOpen(false), position: "left-top", containerClass: `${baseClass}__group-by-submenu` },
                        React.createElement("ul", { role: "menu", className: `${baseClass}__group-by-options` }, grouping.options.map((opt) => {
                            const isActive = grouping.value === opt.value;
                            return (React.createElement("li", { key: opt.value, role: "none" },
                                React.createElement("button", { type: "button", role: "menuitem", className: [
                                        `${baseClass}__group-by-option`,
                                        isActive ? `${baseClass}__group-by-option--selected` : ''
                                    ]
                                        .filter(Boolean)
                                        .join(' '), onClick: () => {
                                        grouping.onChange(opt.value);
                                        setGroupBySubmenuOpen(false);
                                    } },
                                    isActive && React.createElement("i", { className: "fa-light fa-check", "aria-hidden": "true" }),
                                    opt.label)));
                        }))))),
                React.createElement("div", { className: `${baseClass}__actions` },
                    React.createElement(Button, { type: "button", onClick: handleRestoreDefaults },
                        React.createElement("i", { className: "fa-light fa-arrow-rotate-left", "aria-hidden": "true" }),
                        l.restoreDefaults))))));
}
ColumnVisibilityControl.displayName = 'ColumnVisibilityControl';
